import React, { Component } from 'react';
import { connect } from 'react-redux';
import PubNub from 'pubnub';
import styled from 'styled-components';

import ConsultationList from '../components/ConsultationList';
import FilterPane from '../components/FilterPane';
import DashboardHeader from '../components/DashboardHeader';
import ConsultationContainer from './ConsultationContainer';
import ProfileContainer from './ProfileContainer';
import { BoldText } from '../components/common/Text';
import VideoCallContainer from '../components/VideoCallContainer';
import DyteVideoContainer from '../components/DyteVideoContainer';
import { initializeChatClient } from '../services/chatClient';
import { initializeAppClient } from '../services/appClient';
import {
  endVideoCall,
  initiateVideoCall,
  setUnrepliedChannel,
} from '../actions';
import ChangeInsurer from '../components/ChangeInsurer';
import DoctorInfoUpdate from '../components/DoctorInfoUpdate';
import DyteBridge from './DyteBridge';
import IciciInstantPanel from '../components/IciciInstantPanel';
import IciciCaseNotification from '../components/IciciCaseNotification';
import { doCompleteCall, loadDoctorState } from '../actions/iciciQueue';
import { onDoctorConnect } from '../services/iciciInstantQueue';
import config from '../config';

const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: stretch;
`;

const OfflineBar = styled.div`
  background: #ec5d57;
  padding: 6px 0;
  color: white;
  text-align: center;
`;

const ReconnectedBar = styled(OfflineBar)`
  background: #56c02c;
`;

const OfflineText = styled(BoldText)`
  font-size: 1.8rem;
`;

const BodyContainer = styled.div`
  display: flex;
  flex: 1 0;
  align-items: stretch;
  overflow-y: hidden;
`;

class DashboardHome extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showReconnectedBar: false,
      showInfoUpdateModal: false,
      showConsentModal: false,
      showIciciPanel: false,
      iciciCaseNotification: null, // { consultationId, customerName, applicationId, gender, phone, waitSeconds }
      dismissedCaseIds: {}, // consultationIds the doctor has manually dismissed — keyed by id
    };
    this.iciciPubNub = null;
  }

  dispatch(action) {
    this.props.dispatch(action);
  }

  componentDidMount() {
    this.dispatch(endVideoCall());
    if (!this.props.user.authToken) {
      this.props.history.push('/');
      return;
    } else {
      this.chatClient = initializeChatClient();
      this.appClient = initializeAppClient();
      // this.channel = this.props.unrepliedChannel ? this.props.unrepliedChannel : `visit-inbound-doctor-${this.props.user.doctorId}`;
      this.channel = this.props.unrepliedChannel;

      if (!this.props.unrepliedChannel) {
        this.dispatch(setUnrepliedChannel(this.channel));
      }
      console.log({ props: this.props });
      this.chatClient.subscribe(this.channel);
      if (
        ([355, 94, 1450, 199, 1451].includes(this.props.user.doctorId) &&
          this.props.user.insurer === 'ipru') || (this.props.user.isDoctorAvailableForCall && this.props.user.insurer === 'kotak-vfmr')
      ) {
        this.appClient.subscribe('app');
      }

      // Subscribe to ICICI instant queue notifications — only for real doctors (not admin/qc accounts)
      if (this.props.user.insurer === 'ipru' && this.props.user.doctorId && !this.props.user.admin && !this.props.user.qc) {
        this.setupIciciNotifications(this.props.user.doctorId);
        this.dispatch(loadDoctorState());
      }

      if (
        !this.props.user.panNo ||
        !this.props.user.email ||
        !this.props.user.language
      ) {
        this.showUpdateInfoModal();
      }
    }
  }

  setupIciciNotifications(doctorId) {
    this.iciciPubNub = new PubNub({
      publishKey:   config.pubnubPublishKey,
      subscribeKey: config.pubnubSubscribeKey,
      uuid: `dashboard-icici-notif-${doctorId}`,
    });
    this.iciciPubNub.addListener({
      message: ({ message }) => {
        if (message.name === 'new_case_inbound') {
          // Don't show if already in a call or doctor already dismissed this specific case
          if (!this.props.videoCallInitiated && !this.state.dismissedCaseIds[message.consultationId]) {
            this.setState({ iciciCaseNotification: message });
          }
        }
        if (message.name === 'case_claimed') {
          // Dismiss if we're showing this specific case
          this.setState((prev) => {
            if (
              prev.iciciCaseNotification &&
              prev.iciciCaseNotification.consultationId === message.consultationId
            ) {
              return { iciciCaseNotification: null };
            }
            return null;
          });
        }
      },
    });
    this.iciciPubNub.subscribe({
      channels: [`icici-instant-doctor-${doctorId}`],
    });
    // Give PubNub ~800ms to establish the subscription, then ask backend
    // to push any waiting case notification if the doctor is already AVAILABLE
    setTimeout(() => {
      onDoctorConnect().catch(() => {});
    }, 800);
  }

  componentDidUpdate(prevProps, prevState) {
    const { appState } = this.props;
    const prevAppState = prevProps.appState;
    if (prevAppState === 'OFFLINE' && appState === 'ONLINE') {
      this.setState(
        {
          showReconnectedBar: true,
        },
        () => {
          setTimeout(() => {
            this.setState({
              showReconnectedBar: false,
            });
          }, 3000);
        },
      );
    }
    if (
      prevProps.videoCallInitiated &&
      !this.props.videoCallInitiated &&
      prevProps.instantConsultationId
    ) {
      this.dispatch(doCompleteCall(prevProps.instantConsultationId));
    }
    // Clear any pending notification when a call starts
    if (!prevProps.videoCallInitiated && this.props.videoCallInitiated) {
      this.setState({ iciciCaseNotification: null });
    }
    // When doctor transitions back to AVAILABLE, clear dismissed list so they get fresh notifications
    const prevStatus = prevProps.iciciDoctorStatus ? prevProps.iciciDoctorStatus.status : null;
    const currStatus = this.props.iciciDoctorStatus ? this.props.iciciDoctorStatus.status : null;
    if (prevStatus !== 'AVAILABLE' && currStatus === 'AVAILABLE') {
      this.setState({ dismissedCaseIds: {} }, () => {
        // Re-request waiting case notification now that dismissedCaseIds is cleared,
        // because the PubNub message may have arrived before this state update.
        onDoctorConnect().catch(() => {});
      });
    }
  }

  componentWillUnmount() {
    if (this.chatClient) {
      this.chatClient.unsubscribe(this.channel);
    }
    if (this.iciciPubNub) {
      this.iciciPubNub.unsubscribeAll();
    }
  }

  initiateVideoCall = () => {
    this.dispatch(initiateVideoCall());
    // this.setState({ videoCallInitiated: true });
  };
  endVideoCall = () => {
    this.dispatch(endVideoCall());
    // this.setState({ videoCallInitiated: false });
  };

  openIciciPanel = () => {
    this.setState({ showIciciPanel: true });
  };

  closeIciciPanel = () => {
    this.setState({ showIciciPanel: false });
  };

  dismissCaseNotification = () => {
    this.setState((prev) => ({
      iciciCaseNotification: null,
      dismissedCaseIds: prev.iciciCaseNotification
        ? { ...prev.dismissedCaseIds, [prev.iciciCaseNotification.consultationId]: true }
        : prev.dismissedCaseIds,
    }));
  };

  hideModal = () => {
    this.setState({ showInfoUpdateModal: false });
  };

  showUpdateInfoModal = () => {
    this.setState({ showInfoUpdateModal: true });
  };
  handleOpenConsentModal = () => {
    this.setState({ showConsentModal: true });
  };
  handleCloseConsentModal = () => {
    this.setState({ showConsentModal: false });
  };
  render() {
    const { appState, user } = this.props;
    const { showReconnectedBar, showInfoUpdateModal, showIciciPanel, iciciCaseNotification } = this.state;
    const loadFilterPane = () => {
      let filterPane = null;
      if (this.props.showFilterPane) {
        filterPane = <FilterPane />;
      }
      return filterPane;
    };

    return (
      <OuterContainer>
        {appState === 'OFFLINE' && (
          <OfflineBar>
            <OfflineText>You are offline</OfflineText>
          </OfflineBar>
        )}
        {showReconnectedBar && (
          <ReconnectedBar>
            <OfflineText>Reconnecting...</OfflineText>
          </ReconnectedBar>
        )}
        <DashboardHeader
          history={this.props.history}
          initiateVideoCall={this.initiateVideoCall}
          showUpdateInfoModal={this.showUpdateInfoModal}
          onOpenIciciPanel={this.openIciciPanel}
        />
        {user.changeInsurer && !this.props.videoCallInitiated ? (
          <ChangeInsurer />
        ) : (
          ''
        )}
        <BodyContainer>
          {loadFilterPane()}
          {!this.props.videoCallInitiated && <ConsultationList />}
          <ConsultationContainer
            videoCallInitiated={this.props.videoCallInitiated}
            showConsentModal={this.state.showConsentModal}
            handleCloseConsentModal={this.handleCloseConsentModal}
          />
          {!this.props.videoCallInitiated && (
            <ProfileContainer
              handleOpenConsentModal={this.handleOpenConsentModal}
            />
          )}
          {this.props.videoCallInitiated && (
            <VideoCallContainer endVideoCall={this.endVideoCall} />
          )}
          {/* {!videoCallInitiated && <ConsultationList />}
          <ConsultationContainer videoCallInitiated={videoCallInitiated} />
          {!videoCallInitiated && <ProfileContainer />}
          {videoCallInitiated && (
            <DyteVideoContainer />
          )} */}
        </BodyContainer>
        {showInfoUpdateModal && <DoctorInfoUpdate hideModal={this.hideModal} />}
        {showIciciPanel && (
          <IciciInstantPanel onClose={this.closeIciciPanel} />
        )}
        {iciciCaseNotification && !this.props.videoCallInitiated && (
          <IciciCaseNotification
            caseData={iciciCaseNotification}
            onDismiss={this.dismissCaseNotification}
          />
        )}
      </OuterContainer>
    );
  }
}

const mapStateToProps = (state) => ({
  activeConsult: state.consults.activeConsult,
  unrepliedChannel: state.consults.unrepliedChannel,
  showFilterPane: state.app.filterPane,
  user: state.user,
  pubnubChannelGroup: state.info.pubnubChannelGroup,
  appState: state.app.appState,
  videoCallInitiated: state.videoCall.videoCallInitiated,
  instantConsultationId: state.videoCall.instantConsultationId,
  iciciDoctorStatus: state.iciciQueue ? state.iciciQueue.doctorStatus : null,
});

export default connect(mapStateToProps)(DashboardHome);
